/**
 * @file CaseStudies.tsx
 * @description Case studies page highlighting real-world Ultimind deployments.
 */

/**
 * CaseStudiesPage component showcases selected Ultimind customer stories.
 */
export default function CaseStudiesPage() {
  const cases = [
    {
      title: 'Verdant.AI – Automated Source of Wealth Analysis',
      industry: 'Private banking · Financial services',
      impact:
        'Hours of manual review reduced to minutes, with consistent narratives and full audit trails.',
      description:
        'Verdant.AI leverages LLMs, VLMs, and RAG to automatically interpret payslips, tax returns, investment portfolios, and property records. Multi-agent workflows validate figures, reconcile inconsistencies, and compile regulator-ready SoW reports.',
      highlights: [
        'End-to-end automation from data ingestion to report generation.',
        'Integrated with onboarding systems for seamless KYC.',
        'Transparent reasoning steps and risk flags for compliance teams.',
      ],
    },
    {
      title: 'Resort & Destination Agent for Club-style Tourism',
      industry: 'Culture & tourism',
      impact:
        'Higher guest engagement, improved on-site operations, and new revenue from personalized services.',
      description:
        'Ultimind powers a member-centric app and backend that track visitor activities, enable real-time marketing, and coordinate operations across restaurants, activities, and guest services.',
      highlights: [
        'Scenario-based marketing with activity recommendations and dynamic offers.',
        'Real-time dashboards for resort operations and staff coordination.',
        'Unified content and booking management across multiple venues.',
      ],
    },
    {
      title: 'Smart Scenic Area Platform for Taoyangli',
      industry: 'Cultural destination',
      impact:
        'Integrated branding, merchant resources, and digital services on a single platform.',
      description:
        'Multi-agent workflows integrate tourism services, merchant operations, and visitor services into a one-stop platform, supported by a zero-code smart scenic area system.',
      highlights: [
        'Unified place information, digital tickets, and smart vacation cards.',
        'Operational big data used for planning and experience optimization.',
        'Open ecosystem for local merchants and service providers.',
      ],
    },
  ]

  return (
    <div className="bg-slate-950">
      <section className="mx-auto max-w-5xl px-4 pb-16 pt-10 md:px-6 md:pt-14">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-50 md:text-4xl">
          Case Studies
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-300 md:text-base">
          Ultimind partners with leading enterprises and institutions to bring
          Agentic AI from prototypes into mission-critical production systems.
        </p>

        <div className="mt-10 space-y-8">
          {cases.map((item) => (
            <article
              key={item.title}
              className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-300">
                {item.industry}
              </p>
              <h2 className="mt-2 text-lg font-semibold text-slate-50 md:text-xl">
                {item.title}
              </h2>
              <p className="mt-2 text-xs text-emerald-300 md:text-sm">
                Impact: {item.impact}
              </p>
              <p className="mt-3 text-xs text-slate-300 md:text-sm">
                {item.description}
              </p>
              <ul className="mt-3 space-y-2 text-xs text-slate-300 md:text-sm">
                {item.highlights.map((point) => (
                  <li key={point} className="flex gap-2">
                    <span className="mt-[6px] h-1.5 w-1.5 flex-none rounded-full bg-sky-400" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
